import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useState, useEffect, React } from "react";
import { Routes, Route } from "react-router-dom";

import { T } from "./constants/theme";
import { apiFetch } from "./services/api";
import Footer from "./components/Footer";
import { ToastProvider } from "./contexts/ToastContext";
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { AuthProvider } from "./contexts/AuthContext";
import { PaymentContext } from "./contexts/PaymentContext";
import CartDrawer from "./components/CartDrawer";
import Navbar from "./components/Navbar";
import ScrollToTop from "./components/ScrollToTop";
import { RequireAuth, RequireAdmin } from "./routes/guards";
import ShopPage from "./pages/customer/ShopPage";
import ProductDetail from "./pages/customer/ProductDetail";
import AuthPage from "./pages/customer/AuthPage";
import OrdersPage from "./pages/customer/OrdersPage";
import HomePage from "./pages/customer/HomePage";
import ExplorePage from "./pages/customer/ExplorePage";
import AdvicePage from "./pages/customer/AdvicePage";
import VirtualTryOnPage from "./pages/customer/VirtualTryOnPage";
import ProfilePage from "./pages/customer/ProfilePage";
import AdminSecurityPage from "./pages/admin/AdminSecurityPage";
import AdminConsolePage from "./pages/admin/AdminConsolePage";
import AdminOrdersPage from "./pages/admin/AdminOrdersPage";
import AdminProfilePage from "./pages/admin/AdminProfilePage";
import NotFoundPage from "./pages/NotFoundPage";

// ─── ROUTER ───────────────────────────────────────────────────

function Router() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.paper,
        fontFamily: "'Crimson Text', Georgia, serif",
      }}
    >
      <style>{`
        * { box-sizing: border-box; }
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@400;500;600;700&display=swap');
        body { margin: 0; font-family: 'DM Sans', sans-serif; }
        @keyframes slideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes chipPulse { from { transform: translateY(0); } to { transform: translateY(-2px); } }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #f3f2ee; }
        ::-webkit-scrollbar-thumb { background: #c4c2bb; border-radius: 3px; }
        input::placeholder { color: #c4c2bb; }
        .shop-chip { transition: transform .18s ease, box-shadow .18s ease; }
        .shop-chip:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.06); }
        .trend-card { transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
        .trend-card:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(0,0,0,0.06); border-color: #ECE7DF; }
        .product-card-shell { animation: slideUp .3s ease both; }
        @media (min-width: 900px) {
          .mobile-shop-actions { display: none !important; }
        }
      `}</style>
      <ScrollToTop />
      <Navbar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/lookbook" element={<ShopPage />} />
        <Route path="/advice" element={<AdvicePage />} />
        <Route path="/virtual-tryon" element={<VirtualTryOnPage />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />
        <Route path="/admin-login" element={<AuthPage mode="admin-login" />} />

        <Route element={<RequireAuth />}>
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>

        <Route element={<RequireAdmin />}>
          <Route path="/admin" element={<AdminConsolePage />} />
          <Route path="/admin/orders" element={<AdminOrdersPage />} />
          <Route path="/admin/security" element={<AdminSecurityPage />} />
          <Route path="/admin/profile" element={<AdminProfilePage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      <CartDrawer />
    </div>
  );
}

// ─── APP ROOT ────────────────────────────────────────────────
export default function App() {
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    let mounted = true;
    apiFetch("/payments/config")
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!mounted || !cfg) return;
        const enabled = Boolean(cfg.stripeEnabled && cfg.publishableKey);
        setStripeEnabled(enabled);
        setStripePromise(enabled ? loadStripe(cfg.publishableKey) : null);
      })
      .catch(() => {
        if (!mounted) return;
        setStripeEnabled(false);
        setStripePromise(null);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <PaymentContext.Provider value={{ stripeEnabled }}>
        <ToastProvider>
          <AuthProvider>
            <CartProvider>
              <WishlistProvider>
                <Router />
              </WishlistProvider>
            </CartProvider>
          </AuthProvider>
        </ToastProvider>
      </PaymentContext.Provider>
    </Elements>
  );
}
